import { useEffect, useState } from 'react';
import {
  AlertTriangle,
  AlertCircle,
  Info,
  RefreshCw,
  Filter,
  X,
  ChevronLeft,
  ChevronRight,
  Calendar,
  ExternalLink,
  CheckCircle,
  CheckSquare,
  Square,
} from 'lucide-react';
import { ConfirmModal } from '../components/ConfirmModal';
import { AlertModal } from '../components/AlertModal';

interface Issue {
  id: string;
  category: string;
  severity: 'critical' | 'error' | 'warning' | 'info';
  log_level: string;
  message: string;
  // BatchLogger writes structured data into the `context` JSONB column.
  // The legacy `details` column exists in the table but is unused by the
  // standard logging path; we read `context` here.
  context: Record<string, any> | null;
  batch_id: string | null;
  contact_id: string | null;
  contact_phone: string | null;
  resolved: boolean;
  resolved_at: string | null;
  resolved_by: string | null;
  created_at: string;
}

interface IssueDetailResponse {
  batch: {
    id: string;
    batch_name: string | null;
    ghl_location_id: string | null;
    status: string | null;
    created_at: string;
  } | null;
  location: {
    locationId: string;
    billingDisplayName: string | null;
  } | null;
}

interface IssueStats {
  totalUnresolved: number;
  totalResolved: number;
  bySeverity: Record<string, number>;
  byCategory: Record<string, number>;
  trend: Record<string, number>;
}

interface FilterOptions {
  category: string;
  severity: string;
  logLevel: string;
  resolved: string;
  startDate: string;
  endDate: string;
}

const defaultFilters: FilterOptions = {
  category: '',
  severity: '',
  logLevel: '',
  resolved: 'false',
  startDate: '',
  endDate: '',
};

export default function AdminIssues({ role }: { role?: string }) {
  const canResolve = role !== 'viewer';
  const [issues, setIssues] = useState<Issue[]>([]);
  const [stats, setStats] = useState<IssueStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination
  const [totalCount, setTotalCount] = useState(0);
  const [limit] = useState(25);
  const [offset, setOffset] = useState(0);

  // Filters
  const [filters, setFilters] = useState<FilterOptions>(defaultFilters);
  const [showFilters, setShowFilters] = useState(false);
  const [categories, setCategories] = useState<{ value: string; label: string }[]>([]);
  const [severities, setSeverities] = useState<{ value: string; label: string }[]>([]);
  const [logLevels, setLogLevels] = useState<{ value: string; label: string }[]>([]);

  // Detail view
  const [selectedIssue, setSelectedIssue] = useState<Issue | null>(null);
  const [selectedDetail, setSelectedDetail] = useState<IssueDetailResponse | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Bulk selection + resolve actions
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [actionLoading, setActionLoading] = useState(false);
  const [confirmFilterResolve, setConfirmFilterResolve] = useState(false);
  const [alert, setAlert] = useState<
    { title: string; message: string; variant: 'error' | 'success' | 'info' } | null
  >(null);

  // Fetch enriched detail (batch + location lookup) when an issue is opened
  useEffect(() => {
    if (!selectedIssue) {
      setSelectedDetail(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingDetail(true);
      setSelectedDetail(null);
      try {
        const response = await fetch(
          `${import.meta.env.VITE_API_URL}/admin/issues/${selectedIssue.id}`,
          { credentials: 'include' }
        );
        const data = (await response.json()) as any;
        if (!cancelled && data.success) {
          setSelectedDetail({ batch: data.batch ?? null, location: data.location ?? null });
        }
      } catch (err) {
        console.error('[Issues] Detail fetch error:', err);
      } finally {
        if (!cancelled) setLoadingDetail(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedIssue]);

  useEffect(() => {
    fetchCategories();
    fetchStats();
  }, []);

  useEffect(() => {
    fetchIssues();
  }, [offset, filters]);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/admin/issues/categories`, {
        credentials: 'include',
      });

      const data = (await response.json()) as any;

      if (data.success) {
        setCategories(data.categories || []);
        setSeverities(data.severities || []);
        setLogLevels(data.logLevels || []);
      }
    } catch (err) {
      console.error('[Issues] Categories error:', err);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/admin/issues/stats`, {
        credentials: 'include',
      });

      const data = (await response.json()) as any;

      if (data.success) {
        setStats(data.stats);
      }
    } catch (err) {
      console.error('[Issues] Stats error:', err);
    }
  };

  const fetchIssues = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      params.append('limit', limit.toString());
      params.append('offset', offset.toString());

      if (filters.category) params.append('category', filters.category);
      if (filters.severity) params.append('severity', filters.severity);
      if (filters.logLevel) params.append('logLevel', filters.logLevel);
      if (filters.resolved !== '') params.append('resolved', filters.resolved);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);

      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/admin/issues?${params.toString()}`,
        { credentials: 'include' }
      );

      const data = (await response.json()) as any;

      if (data.success) {
        setIssues(data.issues || []);
        setTotalCount(data.totalCount || 0);
        setSelectedIds(new Set()); // selection is page/filter-scoped; reset on reload
      } else {
        setError(data.error || 'Failed to fetch issues');
      }
    } catch (err) {
      console.error('[Issues] Error:', err);
      setError('Failed to fetch issues');
    } finally {
      setLoading(false);
    }
  };

  // ── Bulk selection helpers ────────────────────────────────────────────
  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const pageAllSelected =
    issues.length > 0 && issues.every((i) => selectedIds.has(i.id));

  const toggleSelectAllOnPage = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const pageIds = issues.map((i) => i.id);
      if (pageIds.every((id) => next.has(id))) {
        pageIds.forEach((id) => next.delete(id));
      } else {
        pageIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  // ── Resolve actions ───────────────────────────────────────────────────
  const resolveOne = async (id: string, resolved: boolean) => {
    setActionLoading(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/admin/issues/${id}/resolve`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ resolved }),
      });
      const data = (await response.json()) as any;
      if (!data.success) {
        setAlert({ title: 'Action failed', message: data.error || 'Could not update issue', variant: 'error' });
        return;
      }
      // Reflect immediately in the open modal, then refresh list + stats.
      setSelectedIssue((prev) =>
        prev && prev.id === id
          ? { ...prev, resolved, resolved_at: resolved ? new Date().toISOString() : null, resolved_by: resolved ? (prev.resolved_by || null) : null }
          : prev
      );
      await Promise.all([fetchIssues(), fetchStats()]);
    } catch {
      setAlert({ title: 'Action failed', message: 'Network error updating issue', variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const bulkResolveSelected = async (resolved: boolean) => {
    if (selectedIds.size === 0) return;
    setActionLoading(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/admin/issues/bulk-resolve`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: Array.from(selectedIds), resolved }),
      });
      const data = (await response.json()) as any;
      if (!data.success) {
        setAlert({ title: 'Bulk action failed', message: data.error || 'Could not update issues', variant: 'error' });
        return;
      }
      await Promise.all([fetchIssues(), fetchStats()]);
      setAlert({
        title: 'Done',
        message: `${(data.resolvedCount ?? 0).toLocaleString()} issue(s) marked ${resolved ? 'resolved' : 'unresolved'}.`,
        variant: 'success',
      });
    } catch {
      setAlert({ title: 'Bulk action failed', message: 'Network error updating issues', variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const resolveByFilter = async () => {
    setConfirmFilterResolve(false);
    setActionLoading(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/admin/issues/resolve-by-filter`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          category: filters.category,
          severity: filters.severity,
          logLevel: filters.logLevel,
          startDate: filters.startDate,
          endDate: filters.endDate,
        }),
      });
      const data = (await response.json()) as any;
      if (!data.success) {
        setAlert({ title: 'Resolve-by-filter failed', message: data.error || 'Could not resolve issues', variant: 'error' });
        return;
      }
      await Promise.all([fetchIssues(), fetchStats()]);
      setAlert({
        title: 'Done',
        message: `${(data.resolvedCount ?? 0).toLocaleString()} issue(s) resolved.`,
        variant: 'success',
      });
    } catch {
      setAlert({ title: 'Resolve-by-filter failed', message: 'Network error resolving issues', variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleFilterChange = (key: keyof FilterOptions, value: string) => {
    setFilters({ ...filters, [key]: value });
    setOffset(0);
  };

  const clearFilters = () => {
    setFilters(defaultFilters);
    setOffset(0);
  };

  const hasActiveFilters =
    filters.category ||
    filters.severity ||
    filters.logLevel ||
    filters.resolved !== 'false' ||
    filters.startDate ||
    filters.endDate;

  // Resolve-by-filter targets the content filters (not the resolved-status filter);
  // require at least one so the action can never sweep the entire log.
  const hasResolvableFilter = !!(
    filters.category ||
    filters.severity ||
    filters.logLevel ||
    filters.startDate ||
    filters.endDate
  );

  const totalPages = Math.ceil(totalCount / limit);
  const currentPage = Math.floor(offset / limit) + 1;

  if (loading && issues.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Issue Log</h1>
          <p className="text-gray-500 mt-1">Monitor and investigate system issues</p>
        </div>
        <div className="flex items-center space-x-3">
          {canResolve && hasResolvableFilter && (
            <button
              onClick={() => setConfirmFilterResolve(true)}
              disabled={actionLoading}
              className="flex items-center space-x-2 px-4 py-2 border border-green-500 text-green-700 bg-green-50 rounded-lg hover:bg-green-100 disabled:opacity-50"
              title="Resolve every issue matching the current filters, across all pages"
            >
              <CheckCircle className="w-4 h-4" />
              <span>Resolve all matching</span>
            </button>
          )}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center space-x-2 px-4 py-2 border rounded-lg ${
              hasActiveFilters
                ? 'border-blue-500 text-blue-600 bg-blue-50'
                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Filter className="w-4 h-4" />
            <span>Filters</span>
            {hasActiveFilters && (
              <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
            )}
          </button>
          <button
            onClick={fetchIssues}
            disabled={loading}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Stats Overview */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Unresolved"
            value={stats.totalUnresolved}
            icon={AlertCircle}
            iconBg="bg-red-100"
            iconColor="text-red-600"
          />
          <StatCard
            title="Critical"
            value={stats.bySeverity.critical || 0}
            icon={AlertTriangle}
            iconBg="bg-red-100"
            iconColor="text-red-600"
          />
          <StatCard
            title="Errors"
            value={stats.bySeverity.error || 0}
            icon={AlertCircle}
            iconBg="bg-orange-100"
            iconColor="text-orange-600"
          />
          <StatCard
            title="Warnings"
            value={stats.bySeverity.warning || 0}
            icon={Info}
            iconBg="bg-yellow-100"
            iconColor="text-yellow-600"
          />
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">{error}</div>
      )}

      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Categories</option>
                {categories.map((cat) => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Severity</label>
              <select
                value={filters.severity}
                onChange={(e) => handleFilterChange('severity', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Severities</option>
                {severities.map((sev) => (
                  <option key={sev.value} value={sev.value}>
                    {sev.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Log Level</label>
              <select
                value={filters.logLevel}
                onChange={(e) => handleFilterChange('logLevel', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Levels</option>
                {logLevels.map((level) => (
                  <option key={level.value} value={level.value}>
                    {level.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={filters.resolved}
                onChange={(e) => handleFilterChange('resolved', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Status</option>
                <option value="false">Unresolved</option>
                <option value="true">Resolved</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Calendar className="w-4 h-4 inline mr-1" />
                Start Date
              </label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => {
                  const newStart = e.target.value;
                  if (newStart > filters.endDate && filters.endDate) {
                    setFilters({ ...filters, startDate: newStart, endDate: newStart });
                    setOffset(0);
                  } else {
                    handleFilterChange('startDate', newStart);
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => {
                  const newEnd = e.target.value;
                  if (newEnd < filters.startDate && filters.startDate) {
                    setFilters({ ...filters, startDate: newEnd, endDate: newEnd });
                    setOffset(0);
                  } else {
                    handleFilterChange('endDate', newEnd);
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {hasActiveFilters && (
            <div className="mt-4 flex justify-end">
              <button
                onClick={clearFilters}
                className="text-sm text-gray-600 hover:text-gray-900"
              >
                Clear all filters
              </button>
            </div>
          )}
        </div>
      )}

      {/* Bulk action bar — visible when rows are selected */}
      {canResolve && selectedIds.size > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 flex items-center justify-between">
          <span className="text-sm text-blue-900">
            <strong>{selectedIds.size.toLocaleString()}</strong> selected
          </span>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => bulkResolveSelected(true)}
              disabled={actionLoading}
              className="flex items-center space-x-1.5 px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              <CheckCircle className="w-4 h-4" />
              <span>Mark resolved</span>
            </button>
            <button
              onClick={() => bulkResolveSelected(false)}
              disabled={actionLoading}
              className="px-3 py-1.5 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Mark unresolved
            </button>
            <button
              onClick={() => setSelectedIds(new Set())}
              disabled={actionLoading}
              className="px-3 py-1.5 text-gray-600 text-sm hover:text-gray-900 disabled:opacity-50"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Issues List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {canResolve && (
                  <th className="px-4 py-3 text-left w-10">
                    <button
                      onClick={toggleSelectAllOnPage}
                      className="text-gray-500 hover:text-gray-700 align-middle"
                      title={pageAllSelected ? 'Deselect all on page' : 'Select all on page'}
                      disabled={issues.length === 0}
                    >
                      {pageAllSelected ? (
                        <CheckSquare className="w-4 h-4" />
                      ) : (
                        <Square className="w-4 h-4" />
                      )}
                    </button>
                  </th>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Severity
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Message
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Batch
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {issues.length === 0 ? (
                <tr>
                  <td colSpan={canResolve ? 7 : 6} className="px-6 py-12 text-center text-gray-500">
                    <AlertCircle className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                    <p>No issues found</p>
                    {hasActiveFilters && (
                      <button
                        onClick={clearFilters}
                        className="text-blue-600 hover:text-blue-800 text-sm mt-2"
                      >
                        Clear filters
                      </button>
                    )}
                  </td>
                </tr>
              ) : (
                issues.map((issue) => (
                  <tr key={issue.id} className="hover:bg-gray-50">
                    {canResolve && (
                      <td className="px-4 py-4 w-10">
                        <button
                          onClick={() => toggleSelect(issue.id)}
                          className="text-gray-400 hover:text-blue-600 align-middle"
                          title={selectedIds.has(issue.id) ? 'Deselect' : 'Select'}
                        >
                          {selectedIds.has(issue.id) ? (
                            <CheckSquare className="w-4 h-4 text-blue-600" />
                          ) : (
                            <Square className="w-4 h-4" />
                          )}
                        </button>
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <SeverityBadge severity={issue.severity} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">{issue.category}</span>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-gray-900 line-clamp-2 max-w-md">
                        {issue.message}
                      </p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {issue.batch_id ? (
                        <span className="text-sm font-mono text-gray-600">
                          {issue.batch_id.substring(0, 8)}...
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-600">
                        {formatTimeAgo(issue.created_at)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => setSelectedIssue(issue)}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          View Details
                        </button>
                        {issue.resolved && (
                          <span className="inline-flex items-center space-x-1 text-xs text-green-700">
                            <CheckCircle className="w-3.5 h-3.5" />
                            <span>Resolved</span>
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalCount > limit && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <p className="text-sm text-gray-700">
              Showing {offset + 1} to {Math.min(offset + limit, totalCount)} of{' '}
              {totalCount.toLocaleString()} issues
            </p>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setOffset(Math.max(0, offset - limit))}
                disabled={offset === 0}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <span className="text-sm text-gray-700">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setOffset(offset + limit)}
                disabled={offset + limit >= totalCount}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Issue Detail Modal */}
      {selectedIssue && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setSelectedIssue(null)}
            />

            <div className="relative inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <div className="flex items-center space-x-3">
                  <SeverityBadge severity={selectedIssue.severity} />
                  <h3 className="text-lg font-semibold text-gray-900">Issue Details</h3>
                </div>
                <button
                  onClick={() => setSelectedIssue(null)}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="px-6 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
                <div>
                  <label className="text-sm font-medium text-gray-500">Category</label>
                  <p className="text-gray-900">{selectedIssue.category}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-500">Message</label>
                  <p className="text-gray-900">{selectedIssue.message}</p>
                </div>

                {/* Customer (resolved via batch.ghl_location_id, context.locationId,
                    or context.accountSid). Shows billing_display_name when available;
                    falls back to raw location_id if no display name is set. */}
                {(selectedDetail?.location || loadingDetail) && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Customer</label>
                    {loadingDetail ? (
                      <p className="text-gray-400 text-sm">Looking up...</p>
                    ) : selectedDetail?.location ? (
                      <p className="text-gray-900 text-sm">
                        {selectedDetail.location.billingDisplayName || (
                          <span className="text-gray-400">(no display name set)</span>
                        )}{' '}
                        <span className="font-mono text-xs text-gray-500">
                          ({selectedDetail.location.locationId})
                        </span>
                      </p>
                    ) : null}
                  </div>
                )}

                {/* Surfaced context fields — the most useful diagnostic fields lifted
                    out of the raw JSON for at-a-glance reading. Each only renders
                    when present. */}
                {selectedIssue.context && typeof selectedIssue.context.error === 'string' && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Error</label>
                    <p className="text-gray-900 text-sm break-words">
                      {String(selectedIssue.context.error)}
                    </p>
                  </div>
                )}

                {selectedIssue.contact_phone && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Contact Phone</label>
                    <p className="text-gray-900 font-mono text-sm">{selectedIssue.contact_phone}</p>
                  </div>
                )}

                {selectedIssue.context && typeof selectedIssue.context.classification === 'string' && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Classification</label>
                    <p className="text-gray-900 text-sm">{String(selectedIssue.context.classification)}</p>
                  </div>
                )}

                {selectedIssue.context && typeof selectedIssue.context.accountSid === 'string' && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Twilio Sub-Account SID</label>
                    <p className="text-gray-900 font-mono text-sm">{String(selectedIssue.context.accountSid)}</p>
                  </div>
                )}

                {selectedIssue.context && typeof selectedIssue.context.eventType === 'string' && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Event Type</label>
                    <p className="text-gray-900 font-mono text-sm">{String(selectedIssue.context.eventType)}</p>
                  </div>
                )}

                {selectedIssue.context && typeof selectedIssue.context.apiKeyId === 'string' && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">API Key ID</label>
                    <p className="text-gray-900 font-mono text-sm">{String(selectedIssue.context.apiKeyId)}</p>
                  </div>
                )}

                {selectedIssue.context && typeof selectedIssue.context.ip === 'string' && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">IP</label>
                    <p className="text-gray-900 font-mono text-sm">{String(selectedIssue.context.ip)}</p>
                  </div>
                )}

                {selectedIssue.batch_id && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Batch</label>
                    <p className="text-gray-900 text-sm flex items-center flex-wrap">
                      {selectedDetail?.batch?.batch_name && (
                        <span className="mr-2">{selectedDetail.batch.batch_name}</span>
                      )}
                      <span className="font-mono text-xs text-gray-500">{selectedIssue.batch_id}</span>
                      <button
                        onClick={() => {
                          // Navigate to batch
                          window.history.pushState({}, '', '/admin/batches');
                          window.dispatchEvent(new PopStateEvent('popstate'));
                        }}
                        className="ml-2 text-blue-600 hover:text-blue-800"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </button>
                    </p>
                  </div>
                )}

                {selectedIssue.contact_id && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Contact ID</label>
                    <p className="text-gray-900 font-mono text-sm">{selectedIssue.contact_id}</p>
                  </div>
                )}

                <div>
                  <label className="text-sm font-medium text-gray-500">Log Level</label>
                  <p className="text-gray-900">{selectedIssue.log_level}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-500">Created</label>
                  <p className="text-gray-900">
                    {new Date(selectedIssue.created_at).toLocaleString()}
                  </p>
                </div>

                {selectedIssue.resolved && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="text-sm text-green-800">
                      Resolved {selectedIssue.resolved_at && `on ${new Date(selectedIssue.resolved_at).toLocaleString()}`}
                      {selectedIssue.resolved_by && ` by ${selectedIssue.resolved_by}`}
                    </p>
                  </div>
                )}

                {/* Raw context. Surfaced fields above are excluded to keep this
                    block compact; the very large rawBody field (Twilio webhook
                    payloads can be many KB) goes into its own collapsed section. */}
                {selectedIssue.context && (() => {
                  const surfaced = new Set(['error', 'classification', 'accountSid', 'eventType', 'apiKeyId', 'ip']);
                  const ctx = selectedIssue.context as Record<string, unknown>;
                  const rawBody = typeof ctx.rawBody === 'string' ? ctx.rawBody : null;
                  const remaining: Record<string, unknown> = {};
                  for (const [k, v] of Object.entries(ctx)) {
                    if (k === 'rawBody') continue;
                    if (surfaced.has(k)) continue;
                    remaining[k] = v;
                  }
                  const hasRemaining = Object.keys(remaining).length > 0;
                  return (
                    <>
                      {hasRemaining && (
                        <div>
                          <label className="text-sm font-medium text-gray-500 mb-2 block">
                            Additional Context
                          </label>
                          <pre className="bg-gray-100 p-4 rounded-lg text-sm text-gray-800 overflow-x-auto">
                            {JSON.stringify(remaining, null, 2)}
                          </pre>
                        </div>
                      )}
                      {rawBody && (
                        <details className="bg-gray-50 border border-gray-200 rounded-lg">
                          <summary className="px-4 py-2 cursor-pointer text-sm font-medium text-gray-700 hover:bg-gray-100">
                            Raw event body ({rawBody.length.toLocaleString()} chars)
                          </summary>
                          <pre className="px-4 pb-4 pt-2 text-xs text-gray-800 overflow-x-auto max-h-96">
                            {(() => {
                              try {
                                return JSON.stringify(JSON.parse(rawBody), null, 2);
                              } catch {
                                return rawBody;
                              }
                            })()}
                          </pre>
                        </details>
                      )}
                    </>
                  );
                })()}
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex items-center space-x-3">
                {canResolve && (
                  selectedIssue.resolved ? (
                    <button
                      onClick={() => resolveOne(selectedIssue.id, false)}
                      disabled={actionLoading}
                      className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                    >
                      Mark unresolved
                    </button>
                  ) : (
                    <button
                      onClick={() => resolveOne(selectedIssue.id, true)}
                      disabled={actionLoading}
                      className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                    >
                      <CheckCircle className="w-4 h-4" />
                      <span>Mark resolved</span>
                    </button>
                  )
                )}
                <button
                  onClick={() => setSelectedIssue(null)}
                  className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirm: resolve all matching the current filter */}
      <ConfirmModal
        isOpen={confirmFilterResolve}
        title="Resolve all matching issues?"
        message={`This marks every UNRESOLVED issue matching the current filters as resolved — across all pages, not just this one${
          totalCount ? ` (about ${totalCount.toLocaleString()} in the current view)` : ''
        }. Switch the Status filter to "Resolved" afterward to review or undo.`}
        confirmLabel="Resolve all"
        cancelLabel="Cancel"
        onConfirm={resolveByFilter}
        onCancel={() => setConfirmFilterResolve(false)}
      />

      {/* Action result / error */}
      <AlertModal
        isOpen={alert !== null}
        title={alert?.title || ''}
        message={alert?.message || ''}
        variant={alert?.variant || 'info'}
        onClose={() => setAlert(null)}
      />
    </div>
  );
}

// Stat Card Component
function StatCard({
  title,
  value,
  icon: Icon,
  iconBg,
  iconColor,
}: {
  title: string;
  value: number;
  icon: typeof AlertCircle;
  iconBg: string;
  iconColor: string;
}) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value.toLocaleString()}</p>
        </div>
        <div className={`${iconBg} p-3 rounded-xl`}>
          <Icon className={`w-5 h-5 ${iconColor}`} />
        </div>
      </div>
    </div>
  );
}

// Severity Badge Component
function SeverityBadge({ severity }: { severity: string }) {
  const getStyles = () => {
    switch (severity) {
      case 'critical':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'error':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'warning':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'info':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getIcon = () => {
    switch (severity) {
      case 'critical':
      case 'error':
        return <AlertTriangle className="w-3 h-3" />;
      case 'warning':
        return <AlertCircle className="w-3 h-3" />;
      default:
        return <Info className="w-3 h-3" />;
    }
  };

  return (
    <span
      className={`inline-flex items-center space-x-1 px-2.5 py-1 rounded-full text-xs font-medium border ${getStyles()}`}
    >
      {getIcon()}
      <span className="capitalize">{severity}</span>
    </span>
  );
}

// Time formatting helper
function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString();
}
